import { NexusService } from 'global-apis/nexus-service'
import { useQuery } from '@tanstack/react-query'
import {
  ListRecurringBillableItemsResponse,
  RecurringBillableItem,
} from '@nxcr-org/web-api-interface/lib/billing_service_pb'
import { useState } from 'react'

export function useCustomerBilling(customerId: string, subscriptionId: string) {
  const [isEditingSubscription, setIsEditingSubscription] = useState(false)
  const [subscriptionAmount, setSubscriptionAmount] = useState(0)

  function useRecurringBillableItems() {
    const {
      data: recurringBillableItems,
      refetch,
    } = useQuery({
      queryKey: ['/recurringBillableItems'],
      queryFn: () =>
        NexusService.listRecurringBillableItems(
          customerId,
          subscriptionId
        ).then((res) => {
          setSubscriptionAmount(
            res.recurringBillableItemsList.find((rbi) => rbi.productType === 1)
              .amount
          )
          return res
        }),
      placeholderData: {} as ListRecurringBillableItemsResponse.AsObject,
      enabled: !!subscriptionId,
    }
    )

    return {
      recurringBillableItems:
        recurringBillableItems?.recurringBillableItemsList,
      refetchBillableItems: refetch,
    }
  }

  function updateSubscriptionAmount(
    recurringBillableItems: RecurringBillableItem.AsObject[]
  ) {
    const {
      data: updateRecurringBillableItems,
      refetch,
    } = useQuery({
      queryKey: ['/updateRecurringBillableItems'],
      queryFn: () =>
        NexusService.updateRecurringBillableItems(
          customerId,
          subscriptionId,
          buildRecurringBillableItems(recurringBillableItems)
        ).then((res) => {
          return res
        }),
        placeholderData: {} as ListRecurringBillableItemsResponse.AsObject,

        refetchOnWindowFocus: false,
        enabled: !!subscriptionId,
      }
    )

    function buildRecurringBillableItems(
      recurringBillableItems: RecurringBillableItem.AsObject[]
    ) {
      const subscriptionRecurringBillableItems = recurringBillableItems.find(
        (rbi) => rbi.productType === 1
      )
      const newRecurringBillableItem: RecurringBillableItem =
        new RecurringBillableItem()
      newRecurringBillableItem.setAmount(subscriptionAmount)
      newRecurringBillableItem.setId(subscriptionRecurringBillableItems.id)
      newRecurringBillableItem.setProductType(
        subscriptionRecurringBillableItems.productType
      )
      return [newRecurringBillableItem]
    }

    return {
      updatedRecurringBillableItems:
        updateRecurringBillableItems?.recurringBillableItemsList,
      doUpdateSubscriptionAmount: refetch,
    }
  }

  function deleteMileagePackage(
    recurringBillableItems: RecurringBillableItem.AsObject[]
  ) {
    const {
      data: success,
      refetch,
    } = useQuery({
      queryKey: ['/deleteMileagePackage'],
      queryFn: () =>
        NexusService.removeMileageRecurringBillableItem(
          customerId,
          subscriptionId,
          getMileagePackageId(recurringBillableItems)
        ).then((res) => {
          return res
        }),

        refetchOnWindowFocus: false,
        enabled: false,
      }
    )

    function getMileagePackageId(
      recurringBillableItems: RecurringBillableItem.AsObject[]
    ) {
      const mileageRecurringBillableItems = recurringBillableItems.find(
        (rbi) => rbi.productType === 23
      )
      return mileageRecurringBillableItems.id
    }

    return {
      success,
      doDeleteMileagePackage: refetch,
    }
  }

  return {
    useRecurringBillableItems,
    updateSubscriptionAmount,
    isEditingSubscription,
    setIsEditingSubscription,
    subscriptionAmount,
    setSubscriptionAmount,
    deleteMileagePackage,
  }
}
